import { useEffect, useRef, useState } from 'react';
import { ProspectHistorySection } from '../profiles/ProspectHistorySection';
import { MessageSelectionDialog } from '../tracker/MessageSelectionDialog';
import { useProspectingAvailability } from '../tracker/useProspectingAvailability';
import './dealKanban.css';
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  InlineError,
  Input,
  Select,
  Textarea,
} from '../../components/ui';
import { Link } from 'react-router';
import { useCompanies, useCreateCompany, useLeads } from '../tracker/hooks';
import { companyToCreate } from './companySuggestion';
import { useCgStatuses } from '../cgOrders/hooks';
import { ProfileEmpty, ProfileRow, ProfileSection } from '../profiles/ProfileSection';
import { companyHref, dealContacts, leadHref, leadName, orderHref } from '../profiles/profileLinks';
import { JourneyStrip } from '../tracker/JourneyStrip';
import { StartedOnLinkedIn } from '../tracker/StartedOnLinkedIn';
import { useDealJourney } from '../tracker/linkedinJourney';
import { useDealOrders } from './dealOrdersList';
import { useDealActivity } from './dealActivityFeed';
import { useCreateDealTask, useDealTasks } from './dealTasks';
import { useTaskDefaults } from '../tasks/hooks';
import { taskErrorMessage } from '../tasks/queries';
import { dueDateProblem } from '../tasks/taskLens';
import { actorLabel, useLeadActivity, useLogLeadActivity } from '../tracker/leadActivity';
import { CHECKLIST_LABELS, checklistFor } from './dealChecklist';
import { Timeline } from '../profiles/Timeline';
import {
  dealChangeItems,
  leadActivityItems,
  mergeTimeline,
  orderItems,
  taskItems,
} from '../profiles/timelineItems';
import { orderFromDealErrorMessage, useCreateOrderFromDeal } from './dealOrders';
import type { CgOrderType } from '../cgOrders/types';
import {
  dealErrorMessage as outcomeErrorMessage,
  useSetDealOutcome,
} from '../tracker/dealOutcomes';
import { CommentsSection } from '../comments/CommentsSection';
import { localIsoDate } from '../myTasks/types';
import { useDeleteDeal, useUpdateDeal } from './hooks';
import { useMoveDeal } from './dealMoves';
import { placeIntent, placeOf, placeOptions } from './kanbanColumns';
import { NOTHING_KNOWN, deleteWarning, useDealAttachments } from './dealDelete';
import { amountError, formatAmount, parseAmount } from './dealAmount';
import { hubspotLinkError, parseHubspotUrl } from './hubspotLink';
import { linkOrderErrorMessage, useLinkOrderToDeal, useUnlinkedOrders } from './dealOrderLink';
import { OutboundPicker } from './OutboundPicker';
import { useOutboundEntities } from './outboundEntities';
import { starChanges } from './starMove';
import { nearestOpenTaskDate } from './nearestTaskDate';
import { DEAL_TABS, dealTabCounts, initialDealTab, tabFilter } from './drawerTabs';
import { ProfileShell } from '../profiles/ProfileShell';
import { ProfileIdentity } from '../profiles/ProfileIdentity';
import { PropertyCard } from '../profiles/ProfileProperty';
import type { DealTab } from './drawerTabs';
import { dealErrorMessage } from './queries';
import { OUTCOME_LABELS, OUTCOME_TONES, PIPELINE_LABELS, isOverdue } from './types';
import type { Deal, DealSection, DealStage, Pipeline } from './types';
import type { ProjectMemberWithProfile } from '../projects/types';
import type { ReactNode } from 'react';

/**
 * The same borderless label/value row the Companies panel uses. Duplicated as
 * a few lines rather than exported out of the 170KB TrackerPage, which would
 * make that file a dependency of every future drawer.
 */
function KeyValueRow({
  label,
  hint,
  children,
}: {
  label: string;
  /** Sits beside the label, quiet — the same slot SaveHint uses elsewhere. */
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="kv-row">
      <div className="kv-label">
        <span>{label}</span>
        {hint}
      </div>
      <div className="kv-value">{children}</div>
    </div>
  );
}

/**
 * Making a task on the deal itself.
 *
 * Hanneza, 9 September: "on the actual Deal Cards there is only a Next action
 * date field — there is no button to actually create a task for the deal
 * itself... not all deal-related to-dos are tied to one specific contact."
 *
 * She is right that the drawer could not make one. It has read the deal's
 * tasks into the Activity timeline since the profiles pass; the only place
 * that could CREATE one was the composer on the Tasks tab, which meant
 * leaving the deal you were looking at.
 *
 * `useCreateDealTask` already existed and is what the Tasks tab calls, so
 * this is the same write from a second place, not a second way to write.
 *
 * ── The assignee default ───────────────────────────────────────────────────
 *
 * Kyu, 9 September: assign it to the deal lead. The Tasks tab defaults to
 * Unassigned because it is a board covering every deal and has no one person
 * to guess at. Here there is one: the person whose deal this is. An
 * unassigned task on a named deal is a task nobody has, and the whole reason
 * the Tasks tab looked empty was work that never got recorded.
 *
 * A deal with no lead falls back to Unassigned rather than guessing.
 */
function AddDealTask({
  dealId,
  defaultAssignee,
  highestPosition,
  people,
  onDone,
  onError,
}: {
  dealId: string;
  /** The deal lead. '' when the deal has none. */
  defaultAssignee: string;
  highestPosition: number;
  people: ProjectMemberWithProfile[];
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const createTask = useCreateDealTask();
  const workspace = useTaskDefaults();
  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState(defaultAssignee);
  const [due, setDue] = useState<string | undefined>(undefined);
  const [titleProblem, setTitleProblem] = useState('');
  const [dueProblem, setDueProblem] = useState('');
  const effectiveDue = due ?? workspace.defaultDueDate ?? '';
  const formRef = useRef<HTMLDivElement>(null);

  // The form mounts BELOW the open tasks, and on a deal that has any it mounts
  // below the fold of its own card. Hanneza, 18 September: the add-task button
  // opened a form whose date field she could not reach. Capping the card gave
  // the card a scroller; this is what puts her at the part of it she asked for.
  //
  // The button's own label stays out of this comment on purpose. drawerTabs
  // asserts slot order by searching this file's source text for it, so a
  // second copy up here reads as the button moving to the top of the drawer.
  //
  // Mount only. A second call on every keystroke would yank the page while
  // somebody is typing into the field it just brought into view.
  useEffect(() => {
    formRef.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  function add() {
    // Every branch ends in something visible: a silent return on an empty
    // title is indistinguishable from a broken button.
    if (createTask.isPending) {
      return;
    }
    onError('');
    if (title.trim() === '') {
      setTitleProblem('Enter a task name.');
      return;
    }
    const dueDate = effectiveDue === '' ? null : effectiveDue;
    const problem = dueDateProblem(dueDate, workspace.requireDueDate);
    if (problem !== null) {
      setDueProblem(problem);
      return;
    }
    createTask.mutate(
      {
        dealId,
        title: title.trim(),
        assigneeId: assignee === '' ? null : assignee,
        dueDate,
        position: highestPosition + 10,
      },
      {
        onSuccess: onDone,
        // The workspace due-date rule is enforced by a trigger, and this form
        // can disagree with it while workspace_settings is still loading, so
        // a refusal is the expected way that disagreement surfaces.
        onError: (cause: Error) => onError(taskErrorMessage(cause.message)),
      },
    );
  }

  return (
    <div className="deal-task-add" ref={formRef}>
      <Input
        label="New task"
        labelHidden
        placeholder="What needs doing?"
        value={title}
        autoFocus
        {...(titleProblem === '' ? {} : { error: titleProblem })}
        onChange={(event) => {
          setTitle(event.target.value);
          setTitleProblem('');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            add();
          }
          if (event.key === 'Escape') {
            onDone();
          }
        }}
      />
      <div className="deal-task-add-row">
        <Select
          label="Assignee"
          labelHidden
          value={assignee}
          options={[
            { value: '', label: 'Unassigned' },
            ...people.map((person) => ({
              value: person.user_id,
              label: person.display_name.trim() === '' ? person.email : person.display_name,
            })),
          ]}
          onChange={(event) => setAssignee(event.target.value)}
        />
        <Input
          label="Due date"
          labelHidden
          type="date"
          value={effectiveDue}
          {...(dueProblem === '' ? {} : { error: dueProblem })}
          onChange={(event) => {
            setDue(event.target.value);
            setDueProblem('');
          }}
        />
        <Button size="sm" onClick={add} disabled={createTask.isPending}>
          {createTask.isPending ? 'Adding…' : 'Add task'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The Deal Drawer.
 *
 * Everything the four-column board deliberately leaves out lives here: the
 * company, the deal lead, the next action date, the ongoing task, and the
 * thread where Kyu, Nez and Alex actually talk about the account.
 *
 * Non-modal like every other drawer in the Hub, so the board stays live behind
 * it and clicking another deal swaps the subject without closing. Fields save
 * on blur; there is no Save bar.
 */

export function DealDrawer({
  deal,
  sections,
  stages,
  people,
  pipeline,
  onClose,
  highlightCommentId = null,
}: {
  deal: Deal;
  sections: DealSection[];
  stages: DealStage[];
  /** Assignable and mentionable people: the sales team. */
  people: ProjectMemberWithProfile[];
  pipeline: Pipeline;
  onClose: () => void;
  /**
   * A comment to open on, scroll to and mark — set when the drawer was opened
   * from an Inbox mention. The drawer starts on Notes, where the thread lives.
   */
  highlightCommentId?: string | null;
}) {
  const { message_enabled } = useProspectingAvailability();
  const updateDeal = useUpdateDeal(pipeline);
  const removeDeal = useDeleteDeal(pipeline);
  const move = useMoveDeal(pipeline);
  // What is hanging off this deal, fetched when the drawer opens rather than
  // when somebody reaches for Delete: the verdict has to be on screen BEFORE
  // the click, or the guard is just a slower way to find out.
  const attachments = useDealAttachments(deal.id);
  // Never undefined downstream. While the read is in flight, or if it never
  // produced a result at all, everything reads as UNKNOWN — which the confirm
  // says out loud and which nothing treats as zero. The delete stays reachable
  // either way: that was the bug Kyu hit, where a failed check hid the button.
  const attached = attachments.data ?? NOTHING_KNOWN;
  // The sentence that goes into the confirm. Empty for a bare duplicate — the
  // case this feature exists for — so it gets a plain confirm rather than a
  // warning about nothing.
  const warning = deleteWarning(attached);
  /**
   * Which tab the drawer is showing. Details, always, on open: it is what
   * somebody opens a deal to change. See drawerTabs.ts.
   */
  const [tab, setTab] = useState<DealTab>(() => initialDealTab(highlightCommentId));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const createCompany = useCreateCompany();
  // The picker is closed until asked for, and the query behind it is disabled
  // until then: most people opening a drawer are not attaching an order, and
  // it reads a table many of them have no RLS access to.
  const [linking, setLinking] = useState(false);
  const unlinkedOrders = useUnlinkedOrders(linking);
  const linkOrder = useLinkOrderToDeal();
  const outbound = useOutboundEntities();
  const [error, setError] = useState('');
  const today = localIsoDate(new Date());
  // Batch 3b. Outcome goes through its own RPC rather than the drawer's
  // save-on-blur, because closing a deal stamps a date and clearing it on
  // reopen is a rule, not a field edit.
  const setOutcome = useSetDealOutcome();
  const [closing, setClosing] = useState<'won' | 'lost' | null>(null);
  const [outcomeNote, setOutcomeNote] = useState('');
  const [closedOn, setClosedOn] = useState(today);

  // The nurture warning. Kyu: "Nurture is a relationship with the person or
  // company, not a particular Deal" — so the badge is read off the linked
  // lead and shown here, where somebody is about to chase.
  const leadsQuery = useLeads();
  const linkedLead = (leadsQuery.data ?? []).find((row) => row.id === deal.lead_id);
  // For the Contacts section: everyone at this deal's company, by the
  // Company field and never by email domain (profileLinks.ts).
  const companiesQuery = useCompanies();
  const contacts = dealContacts(deal, companiesQuery.data ?? [], leadsQuery.data ?? []);
  // Named from the same list the contacts are derived from, so the Company
  // card and the "everyone at this company" hint can never disagree.
  const companyName =
    deal.company_id === null
      ? null
      : ((companiesQuery.data ?? []).find((row) => row.id === deal.company_id)?.name ?? null);
  // For the CG orders section. Both may be refused for someone outside the
  // CG team; the section says so rather than showing nothing.
  const dealOrders = useDealOrders(deal.id);
  const journey = useDealJourney(
    deal.id,
    `${deal.outcome}-${deal.closed_on}-${deal.lead_id}-${dealOrders.dataUpdatedAt}`,
  );
  const cgStatuses = useCgStatuses('all');
  const statusLabel = (id: string) =>
    (cgStatuses.data ?? []).find((status) => status.id === id)?.label ?? id;
  // For the Activity section: the deal's changes, its contact's emails and
  // notes, its tasks, its orders — merged, newest first (profiles/timeline.ts).
  // The comment thread below is NOT folded in: it is on screen already, and
  // showing each note twice would be the same fact said in two places.
  const dealActivity = useDealActivity(deal.id);
  const leadActivity = useLeadActivity(deal.lead_id ?? '');
  const dealTasks = useDealTasks(pipeline, [deal.id]);
  const logLeadActivity = useLogLeadActivity();
  // The LinkedIn half of the checklist is read off the contact's own
  // timeline, already loaded above: a linkedin_added row, from prospecting or
  // from this box, is the answer. See dealChecklist.ts.
  const addedLeads = new Set(
    (leadActivity.data ?? [])
      .filter((row) => row.kind === 'linkedin_added' && row.lead_id !== null)
      .map((row) => row.lead_id ?? ''),
  );
  const checklist = checklistFor(deal, addedLeads);
  // The company this deal names but does not link to. Null unless there is
  // genuinely something to create — see companySuggestion.ts.
  const missingCompany = companyToCreate({
    companyId: deal.company_id,
    leadCompanyName: linkedLead?.company_name,
    companies: outbound.companies,
  });
  // Open tasks for the section below, and where the next one goes. Position
  // is read off everything including the finished ones, so a new task lands
  // after the last one rather than colliding with a completed row.
  // Filtered to this deal: useDealTasks' cache key has no deal id, so while
  // this deal's read runs, or after it fails, the list can be the last deal's.
  const allTasks = (dealTasks.data ?? []).filter((task) => task.deal_id === deal.id);
  const openTasks = allTasks.filter((task) => !task.is_completed);
  // Offered beside Next action, never written without a click (nearestTaskDate.ts).
  const nearestTaskDate = nearestOpenTaskDate(openTasks, deal.next_action_date);
  const highestTaskPosition = allTasks.reduce(
    (highest, task) => Math.max(highest, task.position),
    0,
  );
  const names = new Map(
    people.map((person) => [
      person.user_id,
      person.display_name.trim() === '' ? person.email : person.display_name,
    ]),
  );
  const personName = (id: string | null) =>
    id === null ? 'Nobody' : (names.get(id) ?? 'Someone at TAP');
  const timeline = mergeTimeline(
    dealChangeItems(dealActivity.data ?? [], {
      stageName: (id) => stages.find((row) => row.id === id)?.name ?? 'No stage',
      sectionName: (id) => sections.find((row) => row.id === id)?.name ?? 'No section',
      personName,
      amount: (value) => (value === null ? 'unpriced' : formatAmount(value)),
      outcome: (value) =>
        value === 'open' || value === 'won' || value === 'lost' ? OUTCOME_LABELS[value] : '—',
    }),
    leadActivityItems(
      leadActivity.data ?? [],
      (row) => actorLabel(row, names),
      linkedLead === undefined ? undefined : leadName(linkedLead),
    ),
    taskItems(dealTasks.data ?? [], personName),
    orderItems(dealOrders.data ?? [], statusLabel, orderHref),
  );
  const counts = dealTabCounts({ timeline });
  const [messageActivity, setMessageActivity] = useState<string | null>(null);
  const chosenMessage = (leadActivity.data ?? []).find((entry) => entry.id === messageActivity);
  // Each tab counts what it prints, and both of these tabs print history now.
  // The OPEN counts did not disappear: they moved to the Tasks and CG orders
  // cards in the associations column, next to the lists they describe.
  const taskHistory = counts.tasks ?? 0;
  const orderHistory = counts.orders ?? 0;

  // Batch 3c. A won deal becomes an order without retyping the client, the
  // company and the contact into the New Order drawer.
  const createOrder = useCreateOrderFromDeal();
  const [raisingOrder, setRaisingOrder] = useState(false);
  const [orderNo, setOrderNo] = useState('');
  const [orderMessage, setOrderMessage] = useState('');
  /**
   * Which pipeline this deal becomes.
   *
   * Kyu: "the type decision happens at the handoff, not after the wrong
   * workflow has started." A custom order and a stock order share a client and
   * a number and almost nothing else — starting the wrong one means cancelling
   * it and raising it again, because there is deliberately no way to switch an
   * order's type after the fact.
   */
  const [orderType, setOrderType] = useState<CgOrderType>('custom');

  function report(cause: unknown) {
    setError(dealErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function save(changes: Parameters<typeof updateDeal.mutate>[0]['changes']) {
    setError('');
    updateDeal.mutate({ dealId: deal.id, changes }, { onError: report });
  }

  /**
   * One slice of the merged timeline, drawn WITHOUT its own filter chips —
   * the tab row at the top is the filter now, and two rows of the same
   * choices would be two controls for one decision.
   *
   * Keyed by tab so the "Show older" count starts fresh on each one rather
   * than carrying a previous tab's expansion into a shorter list.
   */
  function feed(which: DealTab, empty?: string) {
    if (dealActivity.isError) {
      return <ProfileEmpty>The change log could not be read.</ProfileEmpty>;
    }
    const filter = tabFilter(which);
    if (filter === null) return null;
    return (
      <Timeline
        key={which}
        items={timeline}
        filter={filter}
        actions={(item) => {
          const entry = (leadActivity.data ?? []).find((row) => item.id === `lead-${row.id}`);
          return message_enabled && entry?.kind === 'linkedin' && entry.source === 'manual' ? (
            <Button variant="ghost" size="sm" onClick={() => setMessageActivity(entry.id)}>
              Contact’s message used
            </Button>
          ) : null;
        }}
        {...(empty === undefined ? {} : { empty })}
      />
    );
  }

  const section = sections.find((row) => row.id === deal.section_id);
  const stage = stages.find((row) => row.id === deal.stage_id);
  const overdue = isOverdue(deal.next_action_date, today);

  return (
    <Drawer open wide title={deal.name} onClose={onClose}>
      {message_enabled && chosenMessage !== undefined && (
        <MessageSelectionDialog
          subject={{ kind: 'deal', id: deal.id }}
          activity={chosenMessage}
          detailActivity={chosenMessage}
          onClose={() => setMessageActivity(null)}
        />
      )}
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {journey.isError && <InlineError>The LinkedIn journey could not be loaded.</InlineError>}
      <JourneyStrip row={journey.data} />

      <ProfileShell
        identity={
          <ProfileIdentity>
            {/* Header bar: where this deal sits, at a glance. */}
            <div className="deal-drawer-badges">
              <Badge tone="neutral">{PIPELINE_LABELS[pipeline]}</Badge>
              {section === undefined ? null : <Badge tone="neutral">{section.name}</Badge>}
              {stage === undefined ? null : <Badge tone="brand">{stage.name}</Badge>}
              {overdue ? <Badge tone="danger">Overdue</Badge> : null}
              <Badge tone={OUTCOME_TONES[deal.outcome]}>
                {OUTCOME_LABELS[deal.outcome]}
                {deal.outcome === 'open' || deal.closed_on === null ? '' : ` ${deal.closed_on}`}
              </Badge>
            </div>

            {/* Nurture belongs to the relationship, not to this deal — so it warns
          here rather than blocking anything. It never pauses a task. */}
            {linkedLead?.nurture_active !== true ? null : (
              <p className="deal-nurture-warning">
                <strong>Nurture active</strong> — review before sending a direct sales follow-up.
                {linkedLead.nurture_note.trim() === '' ? null : ` ${linkedLead.nurture_note}`}
              </p>
            )}
          </ProfileIdentity>
        }
        properties={
          <>
            {/* Where this deal stands and what happens next. Outcome, Section
                and Stage are three answers to one question and were three rows
                apart; Next action is the date the answer is due. */}
            <PropertyCard title="Outcome and stage" icon="tracker" tone="purple">
              <KeyValueRow label="Outcome">
                {closing === null ? (
                  <div className="deal-outcome-actions">
                    {deal.outcome === 'open' ? (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setOutcomeNote('');
                            setClosedOn(today);
                            setClosing('won');
                          }}
                        >
                          Mark won
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setOutcomeNote('');
                            setClosedOn(today);
                            setClosing('lost');
                          }}
                        >
                          Mark lost
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="deal-outcome-note">
                          {deal.closed_on === null
                            ? 'Closed — date not recorded'
                            : `Closed ${deal.closed_on}`}
                          {deal.outcome_note.trim() === '' ? '' : ` · ${deal.outcome_note}`}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={setOutcome.isPending}
                          onClick={() => {
                            setError('');
                            setOutcome.mutate(
                              { dealId: deal.id, outcome: 'open' },
                              { onError: (cause) => setError(outcomeErrorMessage(cause.message)) },
                            );
                          }}
                        >
                          Reopen
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="deal-outcome-form">
                    <Input
                      label={closing === 'won' ? 'Won on' : 'Lost on'}
                      type="date"
                      value={closedOn}
                      onChange={(event) => setClosedOn(event.target.value)}
                    />
                    <Textarea
                      label="Why, in one line (optional)"
                      rows={2}
                      value={outcomeNote}
                      placeholder={
                        closing === 'won'
                          ? 'What they bought'
                          : 'Went with the incumbent, budget, timing…'
                      }
                      onChange={(event) => setOutcomeNote(event.target.value)}
                    />
                    <p className="deal-outcome-note">
                      The stage stays where it is. A lost deal keeps how far it got, so the board
                      can still answer where deals fall over.
                    </p>
                    <div className="deal-outcome-actions">
                      <Button
                        size="sm"
                        disabled={setOutcome.isPending}
                        onClick={() => {
                          setError('');
                          setOutcome.mutate(
                            {
                              dealId: deal.id,
                              outcome: closing,
                              note: outcomeNote,
                              ...(closedOn === '' ? {} : { closedOn }),
                            },
                            {
                              onSuccess: () => setClosing(null),
                              onError: (cause) => setError(outcomeErrorMessage(cause.message)),
                            },
                          );
                        }}
                      >
                        {setOutcome.isPending ? 'Saving…' : `Mark ${closing}`}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setClosing(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </KeyValueRow>

              <KeyValueRow label="Section">
                <Select
                  label="Section"
                  labelHidden
                  value={deal.section_id ?? ''}
                  options={[
                    { value: '', label: 'No section' },
                    ...sections.map((row) => ({ value: row.id, label: row.name })),
                  ]}
                  onChange={(event) =>
                    save({ section_id: event.target.value === '' ? null : event.target.value })
                  }
                />
              </KeyValueRow>

              {/* Where the deal is, the way the board says it. Kyu, 8 September:
          "it should show in the dropdown for deals in the list tab or in the
          deal profile as well." Won and Lost are choices here now, and they
          write exactly what a drop to those columns writes, through the same
          function — so this and the buttons above cannot disagree. The
          buttons stay because they take the note and the date; this takes
          today and no note, as the board does. */}
              <KeyValueRow label="Stage">
                <Select
                  label="Stage"
                  labelHidden
                  value={placeOf(deal)}
                  options={placeOptions(stages)}
                  onChange={(event) => move(deal, placeIntent(deal, event.target.value), report)}
                />
              </KeyValueRow>

              <KeyValueRow label="Next action">
                {/* Keyed on the saved date so the uncontrolled box shows a date
                    the button below wrote. */}
                <Input
                  key={deal.next_action_date ?? ''}
                  label="Next action date"
                  labelHidden
                  type="date"
                  defaultValue={deal.next_action_date ?? ''}
                  onBlur={(event) =>
                    save({
                      next_action_date: event.target.value === '' ? null : event.target.value,
                    })
                  }
                />
                {nearestTaskDate === null ? null : (
                  <div className="deal-company-missing">
                    <span className="deal-company-missing-note">
                      Nearest open task: {nearestTaskDate}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => save({ next_action_date: nearestTaskDate })}
                    >
                      Use this date
                    </Button>
                  </div>
                )}
              </KeyValueRow>
              <KeyValueRow label="Deadline">
                <Input
                  label="Deadline"
                  labelHidden
                  type="date"
                  defaultValue={deal.deadline ?? ''}
                  onBlur={(event) =>
                    save({ deadline: event.target.value === '' ? null : event.target.value })
                  }
                />
              </KeyValueRow>
              <KeyValueRow label="Brief color">
                <Select
                  label="Brief color"
                  labelHidden
                  value={deal.brief_color ?? ''}
                  options={[
                    { value: '', label: 'Auto' },
                    { value: 'red', label: 'Red' },
                    { value: 'yellow', label: 'Yellow' },
                    { value: 'green', label: 'Green' },
                  ]}
                  onChange={(event) =>
                    save({
                      brief_color:
                        event.target.value === ''
                          ? null
                          : (event.target.value as 'red' | 'yellow' | 'green'),
                    })
                  }
                />
              </KeyValueRow>
            </PropertyCard>

            {/* The deal itself. Company and Contact stay HERE rather than
                becoming association cards: both are pickers that set a field
                on the deal, and the contacts themselves are already listed on
                the right. A picker is a property, even when what it picks is
                a record. */}
            <PropertyCard title="About this deal" icon="brief" tone="blue">
              <KeyValueRow label="Deal">
                <Input
                  label="Deal name"
                  labelHidden
                  defaultValue={deal.name}
                  onBlur={(event) => {
                    const next = event.target.value.trim();
                    if (next !== '' && next !== deal.name) {
                      save({ name: next });
                    }
                  }}
                />
              </KeyValueRow>

              {/*
        Amount, in USD. Kyu, 2 Sep: "how hard would it be to add small text line
        at the bottom for the deal amount" — then "per deal, yes" and "yes just
        the dollar amount".

        Blank is UNPRICED, not zero, all the way down: clearing the field saves
        null rather than 0, because a deal nobody has put a number on and a deal
        worth nothing are different facts and the column totals depend on
        telling them apart.

        Anything that is not a number is refused with a message rather than
        saved as something — `parseAmount` returns undefined for that case
        precisely so this branch exists.
      */}
              <KeyValueRow label="Amount">
                <Input
                  label="Amount in USD"
                  labelHidden
                  inputMode="decimal"
                  placeholder="e.g. 12500"
                  defaultValue={deal.amount === null ? '' : String(deal.amount)}
                  onBlur={(event) => {
                    const raw = event.target.value;
                    const parsed = parseAmount(raw);
                    if (parsed === undefined) {
                      setError(amountError(raw));
                      return;
                    }
                    setError('');
                    if (parsed !== deal.amount) {
                      save({ amount: parsed });
                    }
                  }}
                />
                {deal.amount === null ? null : (
                  <span className="deal-outcome-note">{formatAmount(deal.amount)}</span>
                )}
              </KeyValueRow>

              <KeyValueRow label="Deal lead">
                <Select
                  label="Deal lead"
                  labelHidden
                  value={deal.owner_id ?? ''}
                  options={[
                    { value: '', label: 'Unassigned' },
                    ...people.map((person) => ({
                      value: person.user_id,
                      label: person.display_name.trim() === '' ? person.email : person.display_name,
                    })),
                  ]}
                  onChange={(event) =>
                    save({ owner_id: event.target.value === '' ? null : event.target.value })
                  }
                />
              </KeyValueRow>

              <KeyValueRow label="Company">
                <OutboundPicker
                  label="Company"
                  emptyLabel="No company"
                  entities={outbound.companies}
                  selectedId={deal.company_id}
                  canReachOutbound={outbound.canReachOutbound}
                  onChange={(companyId) => save({ company_id: companyId })}
                />
                {deal.company_id === null ? null : (
                  <Link className="profile-open" to={companyHref(deal.company_id)}>
                    Open
                  </Link>
                )}
                {/*
          Hanneza, 10 September: a company that "should be" in the dropdown and
          is not. It was never a record — the name lives on the contact as
          text. 28 deals were in that state, and every one of them was one
          click from being fixed by somebody who had no way to do it from here.

          The button carries the name FROM THE CONTACT rather than a text box,
          so the record is spelt the way the lead already spells it. Two
          spellings of one company is the failure mode this is avoiding.
        */}
                {missingCompany === null ? null : (
                  <div className="deal-company-missing">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={createCompany.isPending}
                      onClick={() => {
                        setError('');
                        createCompany.mutate(
                          { name: missingCompany, website: '', industry: '' },
                          {
                            onSuccess: (company) => {
                              // Created AND linked. Creating a record the deal still
                              // does not point at would leave her exactly where she
                              // started, with one more row in the dropdown.
                              if (company !== null && company !== undefined) {
                                save({ company_id: company.id });
                              }
                            },
                            onError: (cause: Error) => setError(dealErrorMessage(cause.message)),
                          },
                        );
                      }}
                    >
                      {createCompany.isPending ? 'Adding…' : `+ Add "${missingCompany}"`}
                    </Button>
                    <span className="deal-company-missing-note">
                      Named on the contact, not in the company list yet.
                    </span>
                  </div>
                )}
              </KeyValueRow>

              {/* "Contact", not "Lead": this drawer already calls the internal owner
          the Deal lead, and two different Leads on one screen would be a
          coin-flip every time somebody read it. */}
              <KeyValueRow label="Contact">
                <OutboundPicker
                  label="Contact"
                  emptyLabel="No contact"
                  entities={outbound.leads}
                  selectedId={deal.lead_id}
                  canReachOutbound={outbound.canReachOutbound}
                  onChange={(leadId) => save({ lead_id: leadId })}
                />
                {deal.lead_id === null ? null : (
                  <Link className="profile-open" to={leadHref(deal.lead_id)}>
                    Open
                  </Link>
                )}
              </KeyValueRow>

              {/*
        Hanneza, 9 Sep: a two-item checklist on every deal, old and new, "to
        audit our old leads to make sure we didn't miss this step." Two items,
        two kinds of thing — dealChecklist.ts says why one is an activity on
        the contact and the other a boolean on the deal, and why the first is
        one-way.
      */}
              <KeyValueRow label="Checklist">
                <div className="deal-checklist">
                  <Checkbox
                    label={
                      checklist.linkedin === 'no_contact'
                        ? CHECKLIST_LABELS.linkedinNoContact
                        : CHECKLIST_LABELS.linkedin
                    }
                    checked={checklist.linkedin === 'done'}
                    disabled={checklist.linkedin !== 'todo' || logLeadActivity.isPending}
                    onChange={() => {
                      if (deal.lead_id === null || checklist.linkedin !== 'todo') return;
                      logLeadActivity.mutate(
                        { leadId: deal.lead_id, kind: 'linkedin_added', body: 'Added on LinkedIn' },
                        { onError: report },
                      );
                    }}
                  />
                  <Checkbox
                    label={CHECKLIST_LABELS.hubspot}
                    checked={checklist.hubspot}
                    disabled={updateDeal.isPending}
                    onChange={(event) => save({ hubspot_updated: event.target.checked })}
                  />
                </div>
              </KeyValueRow>

              {/* Alex reads the brief, not the board. This is how a deal gets in
          front of him tomorrow morning without anybody having to remember
          to type it out. Independent of section and pipeline on purpose:
          see 20270127000100. */}
              <KeyValueRow label="CEO brief">
                <Checkbox
                  label="Show this deal in the daily CEO brief"
                  checked={deal.in_ceo_brief}
                  disabled={updateDeal.isPending}
                  /* Through starChanges, not a bare flag write: starring also moves
             the deal, and three screens deciding that separately would
             drift. See starMove.ts. */
                  onChange={() => save(starChanges(deal, sections))}
                />
              </KeyValueRow>

              {/*
        The HubSpot record.

        Hanneza, 9 Sep: HubSpot is the official financial record, the Hub is
        where the team's own notes live, and moving between them meant
        searching HubSpot by name every time.

        A LINK, never a sync. Nothing is read back from HubSpot and no figure
        is copied across, so there is no second copy of the money here to fall
        out of step with theirs.

        Refused rather than saved when the host is not HubSpot's: a field
        labelled HubSpot holding a Google Doc is a mistake nobody sees until
        they click it in front of a client. Clearing the field unlinks, the
        same way clearing Amount unprices.
      */}
              <KeyValueRow label="HubSpot">
                <Input
                  label="HubSpot deal URL"
                  labelHidden
                  type="url"
                  placeholder="Paste the address from HubSpot"
                  defaultValue={deal.hubspot_url ?? ''}
                  onBlur={(event) => {
                    const raw = event.target.value;
                    const parsed = parseHubspotUrl(raw);
                    if (parsed === undefined) {
                      setError(hubspotLinkError(raw));
                      return;
                    }
                    setError('');
                    if (parsed !== deal.hubspot_url) {
                      save({ hubspot_url: parsed });
                    }
                  }}
                />
                {deal.hubspot_url === null ? null : (
                  <a
                    className="deal-hubspot-open"
                    href={deal.hubspot_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in HubSpot
                  </a>
                )}
              </KeyValueRow>
            </PropertyCard>

            {/*
        Delete, ABOVE the thread and behind a confirm.

        Kyu, 1 Sep: "I need to be able to delete a deal... we got dupes we need
        to fix." `deleteDeal` and `useDeleteDeal` have existed since Drop 33
        and nothing ever called them — this is the missing button, not a new
        capability.

        It sat BELOW `CommentsSection` until Kyu reported he could not see it.
        A comment thread has no maximum height, so "last in the drawer" and
        "reachable" are not the same place. Destructive actions still come
        last among the ACTIONS, which is what the rule above the block is for —
        the thread is a different kind of thing and goes after both.

        It lives in the drawer rather than on the board card on purpose. The
        board is where you SPOT a duplicate; the drawer is where you can see
        which copy you are looking at before removing one. A delete control on
        a draggable card is one slip away from deleting the wrong deal.

        Nothing refuses. `attached` is UNKNOWN rather than undefined while the
        check is in flight or after it fails, so the button is always here and
        the confirm carries whatever the check did or did not establish.
      */}
            {/* Always on screen, not behind a tab. The Details tab is gone and
                the record column is where the deal's own controls live; a
                delete you have to find a tab for is the bug this control
                already had once, when it sat under an unbounded comment
                thread. */}
            <div className="deal-delete">
              {confirmDelete ? (
                <div className="drawer-delete-confirm">
                  <span>
                    Delete <strong>{deal.name}</strong>? This cannot be undone.
                    {warning === '' ? '' : ` ${warning}`}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                    Keep it
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={removeDeal.isPending}
                    onClick={() => {
                      removeDeal.mutate(deal.id, {
                        onSuccess: () => {
                          setConfirmDelete(false);
                          onClose();
                        },
                        onError: (cause: unknown) => {
                          setConfirmDelete(false);
                          setError(dealErrorMessage(String(cause)));
                        },
                      });
                    }}
                  >
                    {removeDeal.isPending ? 'Deleting…' : 'Delete deal'}
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
                  Delete this deal…
                </Button>
              )}
            </div>
          </>
        }
        associations={
          <>
            <StartedOnLinkedIn row={journey.data} leadId={deal.lead_id} />
            {/*
        The profile sections. Kyu, 9 September, holding HubSpot next to the
        Hub: "you can see the contacts in there... it will lead you to the
        lead profile. We don't have that in deals." And: "if there's a CG
        order link, it should have its own section as well. Most recent to
        the oldest, top to bottom."

        Below the fields, not above: the fields are what somebody opens a
        deal to change, and a long list here would push them off the screen.
        docs/design-note-profiles-interlinked.md.
      */}
            {/* The company leads, above the contacts.
                Kyu, 15 September: "All of those three should always be seen
                right away." It was a field with an Open link, which tells you
                the value but does not present the company as a record sitting
                beside this deal. The contacts below are "everyone at this
                company", so naming the company first is also what makes that
                hint mean anything. */}
            <PropertyCard
              title="Company"
              icon="workspace"
              tone="green"
              count={deal.company_id === null ? 0 : 1}
            >
              {deal.company_id === null ? (
                <ProfileEmpty>
                  No company on this deal. Set Company above and its leads appear as contacts.
                </ProfileEmpty>
              ) : (
                <ProfileRow
                  href={companyHref(deal.company_id)}
                  name={companyName ?? 'Open company'}
                />
              )}
            </PropertyCard>

            <PropertyCard
              title="Contacts"
              icon="profile"
              tone="green"
              scrolls
              count={contacts.length}
              {...(deal.company_id === null ? {} : { hint: 'everyone at this company' })}
            >
              {contacts.length === 0 ? (
                <ProfileEmpty>
                  No contact yet. Set Contact above, or set a lead's Company to this company and
                  they appear here.
                </ProfileEmpty>
              ) : (
                contacts.map(({ lead, primary }) => (
                  <ProfileRow
                    key={lead.id}
                    href={leadHref(lead.id)}
                    name={
                      <>
                        {leadName(lead)}
                        {primary ? <Badge tone="brand">Primary</Badge> : null}
                      </>
                    }
                    sub={[lead.title, lead.email, lead.phone]
                      .filter((v) => v.trim() !== '')
                      .join(' · ')}
                    {...(lead.last_touch_at === null
                      ? {}
                      : { when: `last touch ${lead.last_touch_at.slice(0, 10)}` })}
                  />
                ))
              )}
            </PropertyCard>
            {/*
        Tasks, FIRST among the sections and directly under Next action.

        Hanneza, 9 September, reported that a Deal Card had no way to make a
        task, so the Deals > Tasks board stayed empty. It sits here rather
        than lower down because the two are read together: Next action is the
        date something is due, a task is the thing somebody does, and putting
        them apart is what made them look like the same field.

        Open tasks only. Finished ones are in Activity below with everything
        else that has happened, and repeating them here would be the same
        fact in two places.
      */}
            <PropertyCard
              title="Tasks"
              icon="tasks"
              tone="amber"
              scrolls
              count={openTasks.length}
              actions={
                addingTask ? null : (
                  <Button variant="ghost" size="sm" onClick={() => setAddingTask(true)}>
                    + Add task
                  </Button>
                )
              }
            >
              {addingTask ? (
                <AddDealTask
                  dealId={deal.id}
                  defaultAssignee={deal.owner_id ?? ''}
                  highestPosition={highestTaskPosition}
                  people={people}
                  onDone={() => setAddingTask(false)}
                  onError={setError}
                />
              ) : null}
              {openTasks.length === 0 && !addingTask ? (
                <ProfileEmpty>
                  Nothing open on this deal. Next action is a date, not a task — add one here and it
                  shows on the Tasks board and in the assignee&rsquo;s My Tasks.
                </ProfileEmpty>
              ) : (
                openTasks.map((task) => (
                  <ProfileRow
                    key={task.id}
                    name={task.title}
                    sub={personName(task.assignee_id)}
                    {...(task.due_date === null
                      ? {}
                      : {
                          pill: {
                            label: task.due_date,
                            tone: isOverdue(task.due_date, today) ? 'danger' : 'neutral',
                          },
                        })}
                  />
                ))
              )}
            </PropertyCard>
            <PropertyCard
              title="CG orders"
              icon="orders"
              tone="orange"
              scrolls
              count={dealOrders.data?.length ?? attached.orderNos.length}
            >
              {dealOrders.isError ? (
                <ProfileEmpty>
                  CG orders could not be read. Only the CG team can see them.
                </ProfileEmpty>
              ) : (
                (dealOrders.data ?? []).map((order) => (
                  <ProfileRow
                    key={order.id}
                    href={orderHref(order.id)}
                    name={`${order.order_no} · ${order.order_type === 'in_stock' ? 'In-Stock' : 'Custom'}`}
                    sub={order.client}
                    pill={{
                      label: statusLabel(order.status),
                      tone: order.date_delivered === null ? 'brand' : 'success',
                    }}
                    when={`placed ${order.created_at.slice(0, 10)}`}
                  />
                ))
              )}
              <div className="deal-order-block">
                {raisingOrder ? (
                  <div className="deal-outcome-form">
                    <Input
                      label="Order number (optional)"
                      placeholder="Leave blank to take the next one"
                      value={orderNo}
                      onChange={(event) => setOrderNo(event.target.value)}
                    />
                    <fieldset className="cg-order-type">
                      <legend>What kind of order is this?</legend>
                      <div className="cg-order-type-options">
                        <label className="cg-order-type-option">
                          <input
                            type="radio"
                            name="deal-order-type"
                            value="custom"
                            checked={orderType === 'custom'}
                            onChange={() => setOrderType('custom')}
                          />
                          <span>
                            <strong>Custom CG Order</strong>
                            <span className="cg-muted">
                              Made to order: approvals, samples, a PO and a supplier.
                            </span>
                          </span>
                        </label>
                        <label className="cg-order-type-option">
                          <input
                            type="radio"
                            name="deal-order-type"
                            value="in_stock"
                            checked={orderType === 'in_stock'}
                            onChange={() => setOrderType('in_stock')}
                          />
                          <span>
                            <strong>In-Stock Bracelet Order</strong>
                            <span className="cg-muted">
                              From warehouse stock: invoice, payment, fulfil, ship.
                            </span>
                          </span>
                        </label>
                      </div>
                    </fieldset>
                    <p className="deal-outcome-note">
                      The company and contact on this deal travel across, and the order starts at
                      the first stage of that pipeline. Line items are added on the order itself —
                      none are invented here.{' '}
                      {
                        'Picking the wrong kind means cancelling the order and raising it again, so it is worth a second look.'
                      }
                      {deal.outcome === 'won'
                        ? ''
                        : ' This deal is not marked won yet. That is allowed, and nothing is changed about the deal.'}
                    </p>
                    <div className="deal-outcome-actions">
                      <Button
                        size="sm"
                        disabled={createOrder.isPending}
                        onClick={() => {
                          setError('');
                          setOrderMessage('');
                          createOrder.mutate(
                            { dealId: deal.id, orderNo, orderType },
                            {
                              onSuccess: (created) => {
                                setRaisingOrder(false);
                                setOrderNo('');
                                // Names the pipeline it actually got, from the
                                // database's answer rather than from what was on
                                // screen — the two can only agree if the write did
                                // what the form asked.
                                setOrderMessage(
                                  `Created ${created.order_no} as ${
                                    created.order_type === 'in_stock' ? 'In-Stock' : 'Custom'
                                  }. Find it in CG Orders.`,
                                );
                              },
                              onError: (cause) =>
                                setError(orderFromDealErrorMessage(cause.message)),
                            },
                          );
                        }}
                      >
                        {createOrder.isPending ? 'Creating…' : 'Create the order'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setRaisingOrder(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : attached.ordersUnknown || attached.orderNos.length === 0 ? (
                  /*
            No order yet — or we could not check. Both ways out sit here
            together, because they answer the same question and Kyu asked it
            out loud on 1 Sep: "I see 'Create CG order' button. would that also
            be the same as Linked to a CG order?"

            It is not, and the difference is expensive: Create raises a SECOND
            order for a job that already has one, which is the duplicate he is
            trying to clean up. Putting Attach anywhere else on the screen —
            it used to be in the footer, below the comment thread — leaves
            Create looking like the only answer.
          */
                  <>
                    <div className="deal-order-actions">
                      <Button variant="secondary" size="sm" onClick={() => setRaisingOrder(true)}>
                        Create CG order
                      </Button>
                      {linking ? null : (
                        <Button variant="ghost" size="sm" onClick={() => setLinking(true)}>
                          Attach an existing order…
                        </Button>
                      )}
                    </div>
                    {linking ? (
                      <div className="deal-order-link">
                        {unlinkedOrders.isPending ? (
                          <p className="tracker-field-note">Looking for orders with no deal…</p>
                        ) : unlinkedOrders.data === undefined ? (
                          <p className="tracker-field-note">
                            Could not read CG orders. Only the CG team can attach one.
                          </p>
                        ) : unlinkedOrders.data.length === 0 ? (
                          <p className="tracker-field-note">
                            Every CG order already has a deal. Nothing to attach.
                          </p>
                        ) : (
                          <Select
                            label="Attach a CG order that has no deal"
                            value=""
                            disabled={linkOrder.isPending}
                            options={[
                              { value: '', label: 'Choose an order…' },
                              ...unlinkedOrders.data.map((order) => ({
                                value: order.id,
                                label: `${order.orderNo} · ${order.client} · ${order.status}`,
                              })),
                            ]}
                            onChange={(event) => {
                              const orderId = event.target.value;
                              if (orderId === '') return;
                              linkOrder.mutate(
                                { orderId, dealId: deal.id },
                                {
                                  onSuccess: () => {
                                    setLinking(false);
                                    setError('');
                                  },
                                  onError: (cause: unknown) => {
                                    setError(linkOrderErrorMessage(String(cause)));
                                  },
                                },
                              );
                            }}
                          />
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setLinking(false)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <p className="deal-outcome-note">
                        <strong>Create</strong> raises a new order and a new number.{' '}
                        <strong>Attach</strong> links an order that already exists and has no deal.
                      </p>
                    )}
                    {orderMessage === '' ? null : (
                      <span className="deal-outcome-note">{orderMessage}</span>
                    )}
                  </>
                ) : (
                  /*
            This deal already has an order, so neither button is offered.
            Showing "Create CG order" beside an existing order is how a job
            ends up with two — and the duplicate orders Kyu is cleaning up are
            the argument for not offering it.
          */
                  <div className="deal-order-actions">
                    {attached.orderNos.map((orderNo) => (
                      <span key={orderNo} className="deal-order-current">
                        {orderNo}
                      </span>
                    ))}
                    <span className="deal-outcome-note">
                      {attached.orderNos.length === 1
                        ? 'Already has an order. Find it in CG Orders.'
                        : 'Already has orders. Find them in CG Orders.'}
                    </span>
                  </div>
                )}
              </div>
            </PropertyCard>
          </>
        }
        timeline={
          <>
            <ProspectHistorySection subject={{ table: 'deals', id: deal.id }} />
            <div className="drawer-tabs" role="tablist" aria-label="Deal">
              {DEAL_TABS.map(({ key, label }) => {
                const count = counts[key];
                const on = tab === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    className={`drawer-tab${on ? ' is-on' : ''}${count === 0 ? ' is-empty' : ''}`}
                    onClick={() => setTab(key)}
                  >
                    {label}
                    {count === null ? null : <span className="drawer-tab-count">{count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Every tab is history now. The open task list and the live CG
                order links moved to the associations column, where the deal's
                other records live; what stays here is what has happened. */}
            {tab !== 'tasks' ? null : (
              <>
                {/* Finished tasks and task events. The list above is what is
              OPEN; this is what already happened. */}
                <ProfileSection title="Task history" count={taskHistory}>
                  {feed('tasks', 'No task has been finished on this deal yet.')}
                </ProfileSection>
              </>
            )}

            {tab !== 'orders' ? null : (
              <>
                <ProfileSection title="Order history" count={orderHistory}>
                  {feed('orders', 'Nothing has happened on an order for this deal yet.')}
                </ProfileSection>
              </>
            )}

            {tab !== 'activity' &&
            tab !== 'emails' &&
            tab !== 'touches' &&
            tab !== 'changes' ? null : (
              <ProfileSection
                title={DEAL_TABS.find((spec) => spec.key === tab)?.label ?? 'Activity'}
                count={counts[tab] ?? 0}
                hint="this deal, its contact, its tasks and orders"
              >
                {feed(tab)}
              </ProfileSection>
            )}

            {/*
        The thread, LAST. Reuses the project/task comment component wholesale,
        so @mentions, attachments and Cmd+V image paste arrive already built
        and already tested rather than reimplemented for deals.

        It goes after the delete rather than before it because it is the one
        block in the drawer with no maximum height. Anything below it is
        reachable only by scrolling a thread of unknown length, which is
        exactly how the delete control went missing.
      */}
            {tab !== 'notes' ? null : (
              <>
                <CommentsSection
                  taskId={null}
                  projectId=""
                  dealId={deal.id}
                  canComment
                  canManage
                  members={people}
                  heading="Notes & discussion"
                  highlightCommentId={highlightCommentId}
                />
                {/* Notes written on the CONTACT, which the thread above does not
              hold. Two sources, so two blocks rather than one list that
              quietly mixes them. */}
                <ProfileSection title="Note history" count={counts.notes ?? 0}>
                  {feed('notes', 'No notes on the contact yet.')}
                </ProfileSection>
              </>
            )}
          </>
        }
      />
    </Drawer>
  );
}
